// MCHD Room Scheduler - Session Management Page
// Main page for admin session management functionality

import { useEffect } from 'react';
import {
  Container,
  Title,
  Text,
  Group,
  Card,
  Stack,
  Badge,
  Button,
  Box,
  Alert,
  Grid,
  Progress,
} from '@mantine/core';
import {
  IconShield,
  IconClock,
  IconDeviceDesktop,
  IconAlertCircle,
  IconWifi,
  IconUsers,
  IconActivity,
} from '@tabler/icons-react';
import { useSessionManagement } from '../../../hooks/admin/useSessionManagement';
import DynamicRefreshButton from '../../../components/atoms/DynamicRefreshButton';
import { SessionTable } from '../../../components/organisms/SessionManagement/SessionTable';
import { Session } from '../../../api/endpoints/auth';

/**
 * Session management page for administrators
 * @returns JSX element
 */
export function SessionManagement(): JSX.Element {
  const {
    sessions,
    recentSessions,
    isLoading,
    error,
    terminateSession,
    terminateAllSessions,
    updateActivity,
    isTerminatingAll,
    stats,
    refetch,
  } = useSessionManagement();

  // Update session activity every 5 minutes
  useEffect(() => {
    const interval = setInterval(() => {
      updateActivity();
    }, 5 * 60 * 1000); // 5 minutes

    return () => clearInterval(interval);
  }, [updateActivity]);

  const handleTerminateSession = (session: Session) => {
    terminateSession(session.sessionId);
  };

  const handleTerminateAllSessions = () => {
    terminateAllSessions();
  };

  const handleViewSession = (session: Session) => {
    // TODO: Implement session details view
    console.log('View session:', session);
  };

  const handleRefresh = () => {
    refetch();
  };

  // Calculate session health score
  const sessionHealthScore = stats.total > 0 
    ? Math.round((stats.active / stats.total) * 100)
    : 100;

  const getHealthColor = (score: number) => {
    if (score >= 80) return 'green';
    if (score >= 60) return 'yellow';
    return 'red';
  };

  const getHealthStatus = (score: number) => {
    if (score >= 80) return 'Healthy';
    if (score >= 60) return 'Warning';
    return 'Critical';
  };

  if (error) {
    return (
      <Container size="xl" py="xl">
        <Alert
          icon={<IconAlertCircle size={16} />}
          title="Error Loading Sessions"
          color="red"
          variant="light"
        >
          {error}
        </Alert>
      </Container>
    );
  }

  return (
    <Container size="xl" py="xl">
      <Stack gap="xl">
        {/* Header */}
        <Group justify="space-between" align="flex-start">
          <Box>
            <Title order={1} mb="xs">
              Session Management
            </Title>
            <Text size="lg" c="dimmed">
              Monitor and manage user sessions across the system
            </Text>
          </Box>
          <Group gap="sm">
            <DynamicRefreshButton
              variant="outline"
              onRefresh={handleRefresh}
              isLoading={isLoading}
              size="sm"
              color="blue"
            />
            <Button
              color="red"
              variant="outline"
              leftSection={<IconShield size={16} />}
              onClick={handleTerminateAllSessions}
              disabled={stats.active === 0 || isTerminatingAll}
              loading={isTerminatingAll}
            >
              Terminate All
            </Button>
          </Group>
        </Group>

        {/* Session Health Overview */}
        <Card shadow="sm" padding="lg" radius="md" withBorder>
          <Group justify="space-between" mb="md">
            <Text size="lg" fw={600}>
              Session Health
            </Text>
            <Badge
              color={getHealthColor(sessionHealthScore)}
              variant="light"
              size="lg"
            >
              {getHealthStatus(sessionHealthScore)}
            </Badge>
          </Group>
          
          <Stack gap="md">
            <Box>
              <Group justify="space-between" mb="xs">
                <Text size="sm" fw={500}>Overall Health Score</Text>
                <Text size="sm" c="dimmed">{sessionHealthScore}%</Text>
              </Group>
              <Progress
                value={sessionHealthScore}
                color={getHealthColor(sessionHealthScore)}
                size="lg"
                radius="md"
              />
            </Box>
            
            <Grid>
              <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
                <Group gap="sm">
                  <IconClock size={20} color="var(--mantine-color-green-6)" />
                  <Box>
                    <Text size="sm" fw={500}>Active Sessions</Text>
                    <Text size="lg" fw={700} c="green">{stats.active}</Text>
                  </Box>
                </Group>
              </Grid.Col>
              
              <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
                <Group gap="sm">
                  <IconActivity size={20} color="var(--mantine-color-blue-6)" />
                  <Box>
                    <Text size="sm" fw={500}>Recent Activity</Text>
                    <Text size="lg" fw={700} c="blue">{stats.recent}</Text>
                  </Box>
                </Group>
              </Grid.Col>
              
              <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
                <Group gap="sm">
                  <IconWifi size={20} color="var(--mantine-color-orange-6)" />
                  <Box>
                    <Text size="sm" fw={500}>Unique IPs</Text>
                    <Text size="lg" fw={700} c="orange">{stats.uniqueIPs}</Text>
                  </Box>
                </Group>
              </Grid.Col>
              
              <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
                <Group gap="sm">
                  <IconDeviceDesktop size={20} color="var(--mantine-color-purple-6)" />
                  <Box>
                    <Text size="sm" fw={500}>Unique Devices</Text>
                    <Text size="lg" fw={700} c="purple">{stats.uniqueDevices}</Text>
                  </Box>
                </Group>
              </Grid.Col>
            </Grid>
          </Stack>
        </Card>

        {/* Session Statistics */}
        <Grid>
          <Grid.Col span={{ base: 12, sm: 6, md: 4 }}>
            <Card shadow="sm" padding="lg" radius="md" withBorder>
              <Group justify="space-between" mb="xs">
                <Text size="sm" fw={500} c="dimmed">
                  Total Sessions
                </Text>
                <IconUsers size={20} color="var(--mantine-color-blue-6)" />
              </Group>
              <Text size="xl" fw={700} c="blue">
                {stats.total}
              </Text>
              <Text size="xs" c="dimmed">
                All user sessions
              </Text>
            </Card>
          </Grid.Col>

          <Grid.Col span={{ base: 12, sm: 6, md: 4 }}>
            <Card shadow="sm" padding="lg" radius="md" withBorder>
              <Group justify="space-between" mb="xs">
                <Text size="sm" fw={500} c="dimmed">
                  Active Sessions
                </Text>
                <IconClock size={20} color="var(--mantine-color-green-6)" />
              </Group>
              <Text size="xl" fw={700} c="green">
                {stats.active}
              </Text>
              <Text size="xs" c="dimmed">
                Currently online
              </Text>
              <Progress
                value={stats.total > 0 ? (stats.active / stats.total) * 100 : 0}
                color="green"
                size="xs"
                mt="xs"
              />
            </Card>
          </Grid.Col>

          <Grid.Col span={{ base: 12, sm: 6, md: 4 }}>
            <Card shadow="sm" padding="lg" radius="md" withBorder>
              <Group justify="space-between" mb="xs">
                <Text size="sm" fw={500} c="dimmed">
                  Recent Activity
                </Text>
                <IconActivity size={20} color="var(--mantine-color-blue-6)" />
              </Group>
              <Text size="xl" fw={700} c="blue">
                {stats.recent}
              </Text>
              <Text size="xs" c="dimmed">
                Last 5 minutes
              </Text>
            </Card>
          </Grid.Col>
        </Grid>

        {/* Session Table */}
        <Card shadow="sm" padding="lg" radius="md" withBorder>
          <Group justify="space-between" mb="md">
            <Text size="lg" fw={600}>
              Active Sessions
            </Text>
            <Group gap="sm">
              <Text size="sm" c="dimmed">
                {stats.active} active session{stats.active !== 1 ? 's' : ''}
              </Text>
            </Group>
          </Group>

          <SessionTable
            sessions={sessions}
            isLoading={isLoading}
            onTerminate={handleTerminateSession}
            onTerminateAll={handleTerminateAllSessions}
            onView={handleViewSession}
            onRefresh={handleRefresh}
            stats={stats}
          />
        </Card>

        {/* Session Insights */}
        <Card shadow="sm" padding="lg" radius="md" withBorder>
          <Text size="lg" fw={600} mb="md">
            Session Insights
          </Text>
          
          <Grid>
            <Grid.Col span={{ base: 12, md: 6 }}>
              <Stack gap="sm">
                <Text size="sm" fw={500}>Device Distribution</Text>
                <Box>
                  <Group justify="space-between" mb="xs">
                    <Text size="sm">Desktop Sessions</Text>
                    <Text size="sm" c="dimmed">
                      {sessions.filter(s => !s.userAgent.includes('Mobile')).length}
                    </Text>
                  </Group>
                  <Progress
                    value={stats.total > 0 ? (sessions.filter(s => !s.userAgent.includes('Mobile')).length / stats.total) * 100 : 0}
                    color="blue"
                    size="sm"
                  />
                </Box>
                <Box>
                  <Group justify="space-between" mb="xs">
                    <Text size="sm">Mobile Sessions</Text>
                    <Text size="sm" c="dimmed">
                      {sessions.filter(s => s.userAgent.includes('Mobile')).length}
                    </Text>
                  </Group>
                  <Progress
                    value={stats.total > 0 ? (sessions.filter(s => s.userAgent.includes('Mobile')).length / stats.total) * 100 : 0}
                    color="green"
                    size="sm"
                  />
                </Box>
              </Stack>
            </Grid.Col>
            
            <Grid.Col span={{ base: 12, md: 6 }}>
              <Stack gap="sm">
                <Text size="sm" fw={500}>Activity Status</Text>
                <Box>
                  <Group justify="space-between" mb="xs">
                    <Text size="sm">Very Recent (0-5 min)</Text>
                    <Text size="sm" c="dimmed">
                      {recentSessions.filter((s: any) => {
                        const lastActivity = new Date(s.lastActivity);
                        const now = new Date();
                        const diffInMinutes = Math.floor((now.getTime() - lastActivity.getTime()) / (1000 * 60));
                        return diffInMinutes < 5;
                      }).length}
                    </Text>
                  </Group>
                  <Progress
                    value={stats.total > 0 ? (recentSessions.filter((s: any) => {
                      const lastActivity = new Date(s.lastActivity);
                      const now = new Date();
                      const diffInMinutes = Math.floor((now.getTime() - lastActivity.getTime()) / (1000 * 60));
                      return diffInMinutes < 5;
                    }).length / stats.total) * 100 : 0}
                    color="green"
                    size="sm"
                  />
                </Box>
                <Box>
                  <Group justify="space-between" mb="xs">
                    <Text size="sm">Idle (5-30 min)</Text>
                    <Text size="sm" c="dimmed">
                      {sessions.filter(s => {
                        const lastActivity = new Date(s.lastActivity);
                        const now = new Date();
                        const diffInMinutes = Math.floor((now.getTime() - lastActivity.getTime()) / (1000 * 60));
                        return diffInMinutes >= 5 && diffInMinutes < 30;
                      }).length}
                    </Text>
                  </Group>
                  <Progress
                    value={stats.total > 0 ? (sessions.filter(s => {
                      const lastActivity = new Date(s.lastActivity);
                      const now = new Date();
                      const diffInMinutes = Math.floor((now.getTime() - lastActivity.getTime()) / (1000 * 60));
                      return diffInMinutes >= 5 && diffInMinutes < 30;
                    }).length / stats.total) * 100 : 0}
                    color="yellow"
                    size="sm"
                  />
                </Box>
              </Stack>
            </Grid.Col>
          </Grid>
        </Card>
      </Stack>
    </Container>
  );
}
